import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { getSignOutTransitionFlag } from '@/auth/sessionStorage';
import { isPublicSitePageEnabled } from '@/publicSite/publicSiteConfig';
import { themeModeToggleEnabled } from '@/theme/themeModeFlags';
import { fontFamilies, iconSizes, s, typographyTokens } from '@/theme/tokens';
import { Box, Inline, Text } from '@/ui';
import { BreadcrumbsProvider } from '@/ui/layout/BreadcrumbsProvider';
import { PublicShell } from '@/ui/layout/PublicShell';
import { Button, CircularProgress } from '@/ui/primitives';
import { AuthenticatedLayout } from '../layouts/AuthenticatedLayout';
import { shouldShowPublicAuthActions } from './appShellRouterUtils';
import type { AdminAccessState, AppBootstrapControllerState } from './AppBootstrapController';
import type { AuthError, AuthSession } from '@/auth/types';
import type { NavItem } from '@/components/sidebarTypes';
import type { PlanAlert, PlanBadge } from '@/shared/plans/planStatus';
import type {
  OrganisationDto,
  OrganisationEntitlementsDto,
  PlanCode,
  ProjectDto,
} from '@flowqa/shared';

type AppShellRouterProps = {
  state: AppBootstrapControllerState;
  session: AuthSession | null;
  authError: AuthError | null;
  adminAccess: AdminAccessState;
  isSuperuser: boolean;
  navItems: NavItem[];
  projects: Array<
    Pick<ProjectDto, 'id' | 'name' | 'slug' | 'badgeChars' | 'badgeColor' | 'badgeEnabled'> & {
      locked: boolean;
      lockReason: string | null;
    }
  >;
  allProjects: Array<
    Pick<ProjectDto, 'id' | 'name' | 'slug' | 'badgeChars' | 'badgeColor' | 'badgeEnabled'> & {
      locked: boolean;
      lockReason: string | null;
    }
  >;
  organisations: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug'>>;
  canCreateProject: boolean;
  planName: string | null;
  planCode: PlanCode | null;
  planAlerts: PlanAlert[];
  planBadge: PlanBadge | null;
  uploadBlockedReason: string | null;
  addOns: OrganisationEntitlementsDto['addOns'] | null;
  lockProjectSelector: boolean;
  onAddProject: () => void;
  onAddOrganisation: () => void;
  onOpenFeedback: () => void;
  onSignOut: () => void;
};

const ROUTE_TRANSITION_LOADING_MIN_MS = 600;

type PendingNavigation = {
  path: string;
  label: string;
};

const normaliseNavigationPath = (href: string): string => {
  try {
    return new URL(href, window.location.origin).pathname;
  } catch {
    return href.split('?')[0] ?? href;
  }
};

const buildPendingNavigationLabel = (label: string): string => {
  return `Loading ${label.toLowerCase()}...`;
};

const RouteTransitionLoadingFallback = ({ label }: { label: string }): ReactElement => (
  <Box
    sx={{
      minHeight: '45vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: s('md') }}>
      <Text role="body" color="textSecondary" align="center">
        {label}
      </Text>
      <CircularProgress size={iconSizes.lg} />
    </Box>
  </Box>
);

export const AppShellRouter = ({
  state,
  session,
  authError,
  adminAccess,
  isSuperuser,
  navItems,
  projects,
  allProjects,
  organisations,
  canCreateProject,
  planName,
  planCode,
  planAlerts,
  planBadge,
  uploadBlockedReason,
  addOns,
  lockProjectSelector,
  onAddProject,
  onAddOrganisation,
  onOpenFeedback,
  onSignOut,
}: AppShellRouterProps): ReactElement => {
  const navigate = useNavigate();
  const [renderedContentPath, setRenderedContentPath] = useState(state.activePath);
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);
  const handleManualSignOut = useCallback((): void => {
    onSignOut();
    void navigate('/sign-in?manual=1', { replace: true });
  }, [navigate, onSignOut]);
  const handleBeforeNavigate = useCallback((href: string, label: string): void => {
    setPendingNavigation({
      path: normaliseNavigationPath(href),
      label: buildPendingNavigationLabel(label),
    });
  }, []);

  useEffect(() => {
    if (renderedContentPath === state.activePath) {
      return;
    }
    const frameId = window.requestAnimationFrame(() => {
      setRenderedContentPath(state.activePath);
    });
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [renderedContentPath, state.activePath]);

  useEffect(() => {
    if (pendingNavigation === null) {
      return;
    }
    if (state.activePath !== pendingNavigation.path) {
      return;
    }
    const timer = window.setTimeout(() => {
      setPendingNavigation(null);
    }, ROUTE_TRANSITION_LOADING_MIN_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [pendingNavigation, state.activePath]);

  const titleContent = (
    <Box
      sx={{
        color: 'inherit',
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: s('sm'),
        paddingY: s('sm'),
      }}
    >
      <Box
        component="img"
        src="/logo.png"
        alt=""
        sx={{ height: '32px', width: '32px', display: 'block' }}
      />
      <Text
        role="sectionTitle"
        component="span"
        sx={{
          fontFamily: fontFamilies.body,
          fontSize: 21,
          lineHeight: 1,
          fontWeight: typographyTokens.bodyStrong.fontWeight,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        FlowQA
      </Text>
    </Box>
  );
  const unauthTitle = (
    <Box component={RouterLink} to="/" sx={{ textDecoration: 'none', color: 'inherit' }}>
      {titleContent}
    </Box>
  );
  const showPublicAuthActions = shouldShowPublicAuthActions(
    state.activePath,
    state.isEntryAuthFlow,
  );
  const showPublicFeaturesLink = isPublicSitePageEnabled('features');
  const showEntryAuthThemeToggle =
    themeModeToggleEnabled &&
    (state.activePath.startsWith('/sign-in') || state.activePath.startsWith('/sign-up'));
  const signOutTransition = getSignOutTransitionFlag();

  return (
    <BreadcrumbsProvider items={state.breadcrumbItems} visible={state.showBreadcrumbs}>
      {state.showAuthenticatedShell ? (
        <AuthenticatedLayout
          session={session}
          isAdmin={adminAccess.canAccess}
          isSuperuser={isSuperuser}
          navItems={navItems}
          sidebarFeatureFlags={state.sidebarFeatureFlags}
          hideSidebar={state.onboardingIncomplete || state.activePath === '/onboarding'}
          showSidebarWhenSignedOut={false}
          forceShowSignOut={Boolean(authError) || (!session && state.showAuthenticatedShell)}
          restrictTopBarToSignOut={state.restrictTopBarToSignOut || signOutTransition}
          disableTopBarSignOut={signOutTransition}
          activePath={state.activePath}
          projects={projects}
          allProjects={allProjects}
          organisations={organisations}
          canCreateProject={canCreateProject}
          planName={planName}
          planCode={planCode}
          planAlerts={planAlerts}
          planBadge={planBadge}
          uploadBlockedReason={uploadBlockedReason}
          addOns={addOns}
          lockProjectSelector={lockProjectSelector}
          onNavigate={(path) => {
            void navigate(path);
          }}
          onAddProject={onAddProject}
          onAddOrganisation={onAddOrganisation}
          onOpenFeedback={onOpenFeedback}
          onBeforeNavigate={handleBeforeNavigate}
          onSignOut={handleManualSignOut}
        >
          {pendingNavigation === null && renderedContentPath === state.activePath ? (
            state.routes
          ) : (
            <RouteTransitionLoadingFallback label={pendingNavigation?.label ?? 'Loading page...'} />
          )}
        </AuthenticatedLayout>
      ) : (
        <PublicShell
          title={unauthTitle}
          pinFooterToViewport={state.activePath !== '/'}
          actions={
            showPublicAuthActions ? (
              <Inline gap="md" align="center" wrap="nowrap" sx={{ width: 'auto', pr: s('sm') }}>
                {showPublicFeaturesLink ? (
                  <Box sx={{ marginRight: s('sm') }}>
                    <Box
                      component={RouterLink}
                      to="/features"
                      sx={{
                        color: 'text.secondary',
                        textDecoration: 'none',
                        '&:hover': {
                          color: 'text.primary',
                          textDecoration: 'underline',
                          textUnderlineOffset: s('sm'),
                        },
                      }}
                    >
                      <Text role="body" component="span">
                        Features
                      </Text>
                    </Box>
                  </Box>
                ) : null}
                <Box sx={{ marginRight: s('sm') }}>
                  <Box
                    component={RouterLink}
                    to="/preview"
                    sx={{
                      color: 'text.secondary',
                      textDecoration: 'none',
                      '&:hover': {
                        color: 'text.primary',
                        textDecoration: 'underline',
                        textUnderlineOffset: s('sm'),
                      },
                    }}
                  >
                    <Text role="body" component="span">
                      Preview
                    </Text>
                  </Box>
                </Box>
                <Button
                  component={RouterLink}
                  to="/sign-in"
                  variant="primary"
                  fullWidthOnMobile={false}
                  sx={{
                    display: {
                      xs: 'none',
                      sm: 'inline-flex',
                    },
                  }}
                >
                  Sign In
                </Button>
                <Button
                  component={RouterLink}
                  to="/sign-up"
                  variant="secondary"
                  fullWidthOnMobile={false}
                >
                  Sign Up
                </Button>
              </Inline>
            ) : showEntryAuthThemeToggle ? (
              <Inline gap="none" align="center" wrap="nowrap" sx={{ width: 'auto', pr: s('sm') }}>
                {null}
              </Inline>
            ) : null
          }
        >
          {state.routes}
        </PublicShell>
      )}
    </BreadcrumbsProvider>
  );
};
